/* eslint-disable prettier/prettier, react/prop-types */
import React, { useState, useEffect, useRef } from "react";
import { collection, getDocs, doc, deleteDoc, updateDoc, addDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase"; 

import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";

import { 
  Grid, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, LinearProgress, Chip, Select, MenuItem, FormControl, InputLabel
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// 🟢 Validates that a string is a valid URL slug
const isValidSlug = (value) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);

export default function SpareParts() {
  const [spareParts, setSpareParts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch user role for permissions
  const userRole = localStorage.getItem("userRole");
  
  // States for Edit Popup 
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState({ id: "", code: "", name: "", slug: "", model: "", price: "", quantity: true, image: "" });
  const [editSlugError, setEditSlugError] = useState("");
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState("");
  const [editUploadProgress, setEditUploadProgress] = useState(0);
  const [editUploading, setEditUploading] = useState(false);
  const editFileInputRef = useRef(null);

  // States for Add Popup 
  const [openAdd, setOpenAdd] = useState(false);
  const [newData, setNewData] = useState({ code: "", name: "", slug: "", model: "", price: "", quantity: true, image: "" });
  const [addSlugError, setAddSlugError] = useState("");
  const [addImageFile, setAddImageFile] = useState(null);
  const [addImagePreview, setAddImagePreview] = useState("");
  const [addUploadProgress, setAddUploadProgress] = useState(0);
  const [addUploading, setAddUploading] = useState(false);
  const addFileInputRef = useRef(null);

  // 🟢 Upload image to Firebase Storage and return download URL
  const uploadImage = (file, folder, onProgress) => {
    return new Promise((resolve, reject) => {
      const timestamp = Date.now();
      const storageRef = ref(storage, `products/${folder}/${timestamp}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on(
        "state_changed",
        (snapshot) => { onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)); },
        (error) => reject(error),
        async () => { resolve(await getDownloadURL(uploadTask.snapshot.ref)); }
      );
    });
  };

  const fetchSpareParts = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "spare_parts"));
      let partsList = querySnapshot.docs.map(document => ({
        id: document.id,
        ...document.data()
      }));

      // 🟢 NEW: Sort the list by 'code' in ascending order before setting the state
      partsList.sort((a, b) => {
        const codeA = a.code ? String(a.code) : "";
        const codeB = b.code ? String(b.code) : "";
        // { numeric: true } ensures correct numerical sorting (e.g., 2 comes before 10)
        return codeA.localeCompare(codeB, undefined, { numeric: true });
      });

      setSpareParts(partsList);
    } catch (error) {
      console.error("Error fetching spare parts: ", error);
    }
    setLoading(false);
  };

  useEffect(() => { fetchSpareParts(); }, []);

  // 🟢 Handle image file selection
  const handleAddImageChange = (e) => { const f = e.target.files[0]; if (!f) return; setAddImageFile(f); setAddImagePreview(URL.createObjectURL(f)); };
  const handleEditImageChange = (e) => { const f = e.target.files[0]; if (!f) return; setEditImageFile(f); setEditImagePreview(URL.createObjectURL(f)); };

  // DELETE FUNCTION
  const handleDelete = async (id) => {
    if (window.confirm("هل تريد حذف قطعة الغيار هذه؟")) {
      await deleteDoc(doc(db, "spare_parts", id));
      fetchSpareParts();
    }
  };

  // EDIT FUNCTIONS
  const handleOpenEdit = (part) => {
    setEditData({ ...part, code: part.code || "", slug: part.slug || "", image: part.image || "" });
    setEditImageFile(null);
    setEditImagePreview(part.image || "");
    setEditSlugError("");
    setOpenEdit(true);
  };

  const handleSaveEdit = async () => {
    if (!editData.slug) { setEditSlugError("الـ Slug مطلوب"); return; }
    if (!isValidSlug(editData.slug)) { setEditSlugError("أحرف إنجليزية صغيرة وأرقام وشرطات فقط (مثال: ricoh-heater-sp201)"); return; }
    setEditSlugError("");
    try {
      let imageURL = editData.image;
      if (editImageFile) { setEditUploading(true); imageURL = await uploadImage(editImageFile, "spare-parts", setEditUploadProgress); setEditUploading(false); }
      const partRef = doc(db, "spare_parts", editData.id);
      await updateDoc(partRef, {
        code: editData.code,
        name: editData.name,
        slug: editData.slug,
        model: editData.model,
        price: Number(editData.price),
        quantity: editData.quantity,
        image: imageURL || ""
      });
      setOpenEdit(false);
      setEditImageFile(null); setEditImagePreview(""); setEditUploadProgress(0);
      fetchSpareParts();
    } catch (e) { setEditUploading(false); alert("حدث خطأ"); }
  };

  // ADD FUNCTIONS
  const handleAddSparePart = async () => {
    if (!newData.slug) { setAddSlugError("الـ Slug مطلوب"); return; }
    if (!isValidSlug(newData.slug)) { setAddSlugError("أحرف إنجليزية صغيرة وأرقام وشرطات فقط (مثال: ricoh-heater-sp201)"); return; }
    setAddSlugError("");
    try {
      let imageURL = newData.image;
      if (addImageFile) { setAddUploading(true); imageURL = await uploadImage(addImageFile, "spare-parts", setAddUploadProgress); setAddUploading(false); }
      await addDoc(collection(db, "spare_parts"), {
        code: newData.code,
        name: newData.name,
        slug: newData.slug,
        model: newData.model,
        price: Number(newData.price),
        quantity: newData.quantity,
        image: imageURL || ""
      });
      setOpenAdd(false);
      setNewData({ code: "", name: "", slug: "", model: "", price: "", quantity: true, image: "" });
      setAddImageFile(null); setAddImagePreview(""); setAddUploadProgress(0);
      fetchSpareParts();
    } catch (error) { setAddUploading(false); alert("حدث خطأ أثناء الإضافة"); }
  };

  // 🟢 Reusable Image Upload Field
  const ImageUploadField = ({ preview, onFileChange, fileInputRef, uploading, progress }) => (
    <Box>
      <input type="file" accept="image/*" style={{ display: "none" }} ref={fileInputRef} onChange={onFileChange} />
      <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={() => fileInputRef.current.click()} fullWidth
        sx={{ textTransform: "none", borderStyle: "dashed", borderWidth: 2, py: 1.5, color: "#1A73E8", borderColor: "#1A73E8" }}>
        {preview ? "تغيير الصورة" : "رفع صورة من جهازك"}
      </Button>
      {uploading && (<Box sx={{ mt: 1 }}><LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 4, height: 8 }} /><Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: "block", textAlign: "center" }}>جاري الرفع... {progress}%</Typography></Box>)}
      {preview && !uploading && (<Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}><img src={preview?.startsWith('/') ? `https://mr-wafaae.com${preview}` : preview} alt="preview" style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, border: "1px solid #ddd" }} /><Chip label="صورة محددة ✓" color="success" size="small" /></Box>)}
    </Box>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ pt: 3, pb: 3, direction: "rtl" }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ p: 3, borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
              
              {/* Header Section */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Typography variant="h5" sx={{ fontWeight: "bold", color: "#344767" }}>
                  مخزن قطع الغيار
                </Typography>
                
                {userRole === "admin" && (
                  <Button 
                    variant="contained" 
                    onClick={() => setOpenAdd(true)}
                    sx={{ 
                      background: "#4CAF50", 
                      color: "#fff", 
                      fontWeight: "bold",
                      "&:hover": { background: "#388E3C" } 
                    }}
                  >
                    + إضافة قطعة غيار
                  </Button>
                )}
              </Box>

              {/* 🟢 FULLY RESPONSIVE TABLE CONTAINER */}
              <TableContainer sx={{ overflowX: "auto", border: "1px solid #f0f2f5", borderRadius: "8px" }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow>
                      <TableCell align="center" sx={{ fontWeight: "bold", color: "#7b809a" }}>الكود</TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold", color: "#7b809a" }}>الصورة</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold", color: "#7b809a" }}>اسم القطعة</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold", color: "#7b809a" }}>Slug</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold", color: "#7b809a" }}>الموديل المناسب</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold", color: "#7b809a" }}>السعر</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold", color: "#7b809a" }}>الكمية</TableCell>
                      
                      {userRole === "admin" && (
                        <TableCell align="center" sx={{ fontWeight: "bold", color: "#7b809a" }}>الإجراءات</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ color: "#7b809a", padding: "20px" }}>جاري تحميل البيانات...</TableCell></TableRow>
                    ) : spareParts.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ color: "#7b809a", padding: "20px" }}>لا توجد قطع غيار في قاعدة البيانات</TableCell></TableRow>
                    ) : (
                      spareParts.map((part) => (
                        <TableRow key={part.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                          
                          {/* 🟢 NEW: Display Code */}
                          <TableCell align="center">
                            <Typography variant="button" fontWeight="bold" color="#ed6c02">
                              {part.code ? `[${part.code}]` : "-"}
                            </Typography>
                          </TableCell>

                          <TableCell align="center">
                            {part.image ? (
                              <img 
                                src={part.image?.startsWith('/') ? `https://mr-wafaae.com${part.image}` : part.image} 
                                alt={part.name} 
                                style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "8px", border: "1px solid #ddd" }} 
                              />
                            ) : (
                              <Box sx={{ width: 50, height: 50, bgcolor: "#f0f2f5", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>
                                <Typography variant="caption" color="textSecondary">لا توجد</Typography>
                              </Box>
                            )}
                          </TableCell>

                          <TableCell align="right">
                            <Typography variant="button" fontWeight="medium" color="#344767">
                              {part.name}
                            </Typography>
                          </TableCell>

                          <TableCell align="right">
                            <Typography variant="caption" sx={{ color: "#7b809a", fontFamily: "monospace", bgcolor: "#f5f5f5", px: 1, py: 0.5, borderRadius: 1 }}>
                              {part.slug || "-"}
                            </Typography>
                          </TableCell>
                          
                          <TableCell align="right" sx={{ color: "#7b809a" }}>{part.model}</TableCell>
                          <TableCell align="right" sx={{ color: "#7b809a" }}>{part.price} ج.م</TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={part.quantity === false ? "غير متوفر" : "متوفر"} 
                              color={part.quantity === false ? "error" : "success"} 
                              size="small" 
                              sx={{ fontWeight: "bold", minWidth: 80 }} 
                            />
                          </TableCell>
                          
                          {userRole === "admin" && (
                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                              <Button 
                                variant="contained"
                                size="small"
                                onClick={() => handleOpenEdit(part)}
                                sx={{ 
                                  marginLeft: "10px",
                                  backgroundColor: "#1A73E8 !important",
                                  color: "#fff !important", 
                                }}
                              >
                                تعديل
                              </Button>

                              <Button 
                                variant="contained"
                                size="small"
                                onClick={() => handleDelete(part.id)}
                                sx={{ 
                                  backgroundColor: "#F44336 !important",
                                  color: "#fff !important", 
                                }}
                              >
                                حذف
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* POPUP FOR ADDING NEW SPARE PART */}
      <Dialog open={openAdd} onClose={() => { setOpenAdd(false); setAddSlugError(""); }} dir="rtl">
        <DialogTitle sx={{ textAlign: "right", fontWeight: "bold" }}>إضافة قطعة غيار</DialogTitle>
        <DialogContent style={{ display: "flex", flexDirection: "column", gap: "15px", paddingTop: "15px", minWidth: "400px" }}>
          
          <TextField label="كود المنتج (اختياري)" fullWidth value={newData.code} onChange={(e) => setNewData({...newData, code: e.target.value})} />

          {/* 🟢 IMAGE UPLOAD BUTTON */}
          <ImageUploadField preview={addImagePreview} onFileChange={handleAddImageChange} fileInputRef={addFileInputRef} uploading={addUploading} progress={addUploadProgress} />

          <TextField label="اسم القطعة (يمكن بالعربي)" fullWidth value={newData.name} onChange={(e) => setNewData({...newData, name: e.target.value})} />

          {/* 🟢 SLUG FIELD WITH VALIDATION */}
          <TextField 
            label="Slug (رابط المنتج)" fullWidth value={newData.slug}
            onChange={(e) => { const v = e.target.value.toLowerCase(); setNewData({...newData, slug: v}); setAddSlugError(v && !isValidSlug(v) ? "أحرف إنجليزية صغيرة وأرقام وشرطات فقط (مثال: ricoh-heater-sp201)" : ""); }}
            error={!!addSlugError} helperText={addSlugError || "يُستخدم في رابط الموقع — مثال: ricoh-heater-sp201"}
            inputProps={{ style: { direction: "ltr", fontFamily: "monospace" } }}
          />

          <TextField label="الموديل المناسب" fullWidth value={newData.model} onChange={(e) => setNewData({...newData, model: e.target.value})} />
          <TextField label="السعر" type="number" fullWidth value={newData.price} onChange={(e) => setNewData({...newData, price: e.target.value})} />
          <FormControl fullWidth>
            <InputLabel>حالة المخزون</InputLabel>
            <Select value={newData.quantity} label="حالة المخزون" onChange={(e) => setNewData({...newData, quantity: e.target.value})}>
              <MenuItem value={true}>✅ متوفر</MenuItem>
              <MenuItem value={false}>❌ غير متوفر</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => { setOpenAdd(false); setAddSlugError(""); }}>إلغاء</Button>
          <Button onClick={handleAddSparePart} variant="contained" color="success" sx={{ color: "white" }} disabled={addUploading}>
            {addUploading ? "جاري الرفع..." : "إضافة للمخزن"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* POPUP FOR EDITING SPARE PART */}
      <Dialog open={openEdit} onClose={() => { setOpenEdit(false); setEditSlugError(""); }} dir="rtl">
        <DialogTitle sx={{ textAlign: "right", fontWeight: "bold" }}>تعديل بيانات قطعة الغيار</DialogTitle>
        <DialogContent style={{ display: "flex", flexDirection: "column", gap: "15px", paddingTop: "15px", minWidth: "400px" }}>
          
          <TextField label="كود المنتج (اختياري)" fullWidth value={editData.code} onChange={(e) => setEditData({...editData, code: e.target.value})} />

          {/* 🟢 IMAGE UPLOAD BUTTON */}
          <ImageUploadField preview={editImagePreview} onFileChange={handleEditImageChange} fileInputRef={editFileInputRef} uploading={editUploading} progress={editUploadProgress} />

          <TextField label="اسم القطعة (يمكن بالعربي)" fullWidth value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} />

          {/* 🟢 SLUG FIELD WITH VALIDATION */}
          <TextField 
            label="Slug (رابط المنتج)" fullWidth value={editData.slug}
            onChange={(e) => { const v = e.target.value.toLowerCase(); setEditData({...editData, slug: v}); setEditSlugError(v && !isValidSlug(v) ? "أحرف إنجليزية صغيرة وأرقام وشرطات فقط (مثال: ricoh-heater-sp201)" : ""); }}
            error={!!editSlugError} helperText={editSlugError || "يُستخدم في رابط الموقع — مثال: ricoh-heater-sp201"}
            inputProps={{ style: { direction: "ltr", fontFamily: "monospace" } }}
          />

          <TextField label="الموديل المناسب" fullWidth value={editData.model} onChange={(e) => setEditData({...editData, model: e.target.value})} />
          <TextField label="السعر" type="number" fullWidth value={editData.price} onChange={(e) => setEditData({...editData, price: e.target.value})} />
          <FormControl fullWidth>
            <InputLabel>حالة المخزون</InputLabel>
            <Select value={editData.quantity} label="حالة المخزون" onChange={(e) => setEditData({...editData, quantity: e.target.value})}>
              <MenuItem value={true}>✅ متوفر</MenuItem>
              <MenuItem value={false}>❌ غير متوفر</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => { setOpenEdit(false); setEditSlugError(""); }}>إلغاء</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="success" sx={{ color: "white" }} disabled={editUploading}>
            {editUploading ? "جاري الرفع..." : "حفظ التعديلات"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}